import { useNavigate } from "react-router-dom";

export default function CalculatorInstructionsPage() {
  const navigate = useNavigate();

  return (
    <div className="app-shell bg-[#020617] text-slate-200 font-['Inter']">
      <header className="app-header bg-[#020617]/90 backdrop-blur border-b border-white/10 px-4 sm:px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-3">
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-white leading-tight">
              Guidelines for Your{" "}
              <span className="text-[#eb6fbd]">
                Financial Runway Calculator
              </span>
            </h1>
            <div className="mt-2 h-1 w-24 bg-[#eb6fbd] rounded" />
          </div>

          <button
            onClick={() => navigate("/dashboard")}
            className="shrink-0 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white/90 hover:bg-white/10 transition text-sm"
          >
            Back
          </button>
        </div>
      </header>

      <main className="app-body px-4 sm:px-6 py-8">
        <div className="guidelines-container max-w-4xl mx-auto p-6 bg-slate-900 text-slate-100 rounded-2xl shadow-md border border-slate-800">
          <h2 className="text-2xl font-bold mb-6 text-[#eb6fbd] border-b border-slate-700 pb-2">
            How to Prepare Your Inputs: 7 Guidelines for an Accurate Runway Calculation
          </h2>

          <ol className="list-decimal pl-5 space-y-6 text-slate-300">
            <li className="pl-2">
              <strong className="text-white block text-lg mb-1">
                Match Your Exact Legal Residency Status
              </strong>
              Start by selecting your current country and your status as either an{" "}
              <strong>Expatriate</strong> or a <strong>National</strong>. The
              engine uses this data node to configure localized labor code
              computations, tax frameworks, and end-of-service benefits unique to
              your location.
            </li>

            <li className="pl-2">
              <strong className="text-white block text-lg mb-1">
                Isolate Your Basic Salary from Fixed Allowances
              </strong>
              When entering your income, pull up your latest payslip or contract.
              Separate your <strong>Monthly Basic Salary</strong> from your{" "}
              <strong>Fixed Allowances</strong>, such as housing or transport.
              Local labor laws calculate gratuity or severance payouts strictly
              using your basic pay scale.
            </li>

            <li className="pl-2">
              <strong className="text-white block text-lg mb-1">
                Estimate Your Transition Window Honestly
              </strong>
              The <strong>Months Until Job Loss/Resignation</strong> field defines
              how much preparation time you have left. If you plan to stay for 6
              months while upskilling, entering 6 allows the calculator to add your
              ongoing income surplus into your final runway fund.
            </li>

            <li className="pl-2">
              <strong className="text-white block text-lg mb-1">
                Input Only Immediately Liquid Assets
              </strong>
              For <strong>Current Cash Savings</strong>, count only cash, checking
              accounts, or emergency funds you can fully access within 48 hours.
              Exclude home equity, long-term retirement portfolios, or locked
              investments.
            </li>

            <li className="pl-2">
              <strong className="text-white block text-lg mb-1">
                Calculate Your Precise Years of Service
              </strong>
              Look up your official continuous service history with your current
              employer. Entering your exact <strong>Years of Service</strong>{" "}
              helps the localized math engine estimate statutory severance or
              end-of-service gratuity more accurately.
            </li>

            <li className="pl-2">
              <strong className="text-white block text-lg mb-1">
                Log Monthly EMIs Separately from Total Outstanding Debt
              </strong>
              To map your obligations correctly, input these into two separate
              tracks:

              <ul className="list-disc pl-5 mt-2 space-y-1 text-slate-400">
                <li>
                  <strong>Monthly Loan EMIs / Debt Service:</strong> Enter the
                  amount you pay every month. This affects your active monthly burn
                  rate.
                </li>
                <li>
                  <strong>Total Outstanding Debt:</strong> Enter the total
                  remaining loan liability. This helps flag liquidity risks during
                  career or visa transitions.
                </li>
              </ul>
            </li>

            <li className="pl-2">
              <strong className="text-white block text-lg mb-1">
                Build a Survival-Mode Expense Baseline
              </strong>
              For groceries, transport, utilities, and other variables, use your
              non-negotiable survival figures, not premium lifestyle spending.
              This reveals your true floor and shows how many months of career
              flexibility you really have.
            </li>
          </ol>

          <div className="pt-8 text-center">
            <button
              onClick={() => navigate("/dashboard")}
              className="px-10 py-3 rounded-full font-bold bg-gradient-to-r from-[#eb6fbd] to-[#d946ef] text-[#0b0b16] shadow-lg shadow-[#eb6fbd]/20 hover:scale-105 transition"
            >
              Continue to Dashboard →
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}